import React from "react";
import styled from "styled-components";
import { alpha, Box, Button, Stack } from "@mui/material";
import { useAuth } from "src/components/providers/auth-context";
import excludeStyleProps from "src/utils/exclude-style-props";
import AvatarBadge from "src/components/avatar-badge";
import Icon from "src/components/icon";
import gitlabIcon from "src/assets/icons/gitlab-int";
import githubIcon from "src/assets/icons/github-int";
import slackIcon from "src/assets/icons/slack-int";
import jiraIcon from "src/assets/icons/jira-int";
import gitlabSelfManagedIcon from "src/assets/icons/gitlab-selfmanaged-int";
import googleWorkspaceIcon from "src/assets/icons/google-workspace-int";
import notionIcon from "src/assets/icons/notion-int";
import TeamMemberCardActions from "./team-member-card-actions";

const Card = styled.button.withConfig({
    shouldForwardProp: excludeStyleProps(["isMe"]),
})`
    width: ${({ expanded }) => (expanded ? "auto" : "276px")};
    min-width: ${({ expanded }) => (expanded ? "320px" : "320px")};
    height: ${({ expanded }) => (expanded ? "auto" : "87px")};
    background-color: ${({ theme }) => theme.palette.background.paper};
    text-transform: none;
    cursor: ${({ isInteractive }) => (isInteractive ? "pointer" : "initial")};
    color: ${({ theme }) => theme.palette.text.primary};
    justify-content: flex-start;
    border: 2px solid rgba(89, 195, 106, 0.25);
    border-radius: 4px;
    border: ${({ theme, isMe, expanded }) => {
        if (isMe && !expanded) return `2px solid ${alpha(theme.palette.primary.main, 0.25)}`;
        return "none";
    }};
    padding: 0;
    line-height: normal;
`;

const Name = styled.span`
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    color: ${({ theme }) => theme.palette.text.primary};
`;

const JobTitle = styled.span`
    font-size: 12px;
    font-weight: 300;
    color: ${({ theme }) => theme.palette.primary.main};
    white-space: nowrap;
`;

const Email = styled.span`
    font-size: 10px;
    font-weight: 300;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const OwnerBadge = styled.div`
    position: absolute;
    width: 48px;
    height: 15px;
    background: rgba(196, 196, 196, 0.1);
    border-radius: 2px;
    font-size: 8px;
    font-weight: 300;
    top: 10px;
    right: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const TeamMemberCard = ({ member, onClick, expanded, isInteractive, onModalClose }) => {
    const { user } = useAuth();

    const { displayName, jobTitle, picture, email, isOwner, isIntegrated, id } = member || {};

    const isInvited = member && member.isInvited;

    return (
        <Card
            onClick={() => isInteractive && onClick(member)}
            isMe={user.id === member.id}
            expanded={expanded}
            isInteractive={isInteractive}
            as={expanded || !isInteractive ? "div" : Button}
        >
            <Box
                p={expanded ? 3 : 1}
                style={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    justifyContent: "baseline",
                    alignItems: "center",
                }}
            >
                {isOwner && <OwnerBadge>Owner</OwnerBadge>}
                <Stack spacing={3} style={{ width: "100%" }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        style={{ height: "100%" }}
                    >
                        {(displayName || email) && (
                            <AvatarBadge
                                name={displayName || email}
                                picture={picture}
                                isUnregistered
                            />
                        )}
                        <Stack
                            direction="column"
                            spacing={0}
                            alignItems="flex-start"
                            style={{ overflow: "hidden" }}
                        >
                            {(displayName || email) && <Name>{displayName || email}</Name>}
                            {jobTitle && <JobTitle>{jobTitle}</JobTitle>}
                            {!id && (
                                <JobTitle>
                                    Unregistered user {isInvited && "+ Invitation pending"}
                                </JobTitle>
                            )}
                            {expanded && email && <Email>{email}</Email>}
                            <Stack direction="row" spacing={2} pt={0.5}>
                                <Icon
                                    svg={gitlabIcon}
                                    fill={
                                        isIntegrated && isIntegrated.gitlab === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={gitlabSelfManagedIcon}
                                    fill={
                                        isIntegrated && isIntegrated.gitlabSelfManaged === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={githubIcon}
                                    fill={
                                        isIntegrated && isIntegrated.github === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={slackIcon}
                                    fill={
                                        isIntegrated && isIntegrated.slack === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={googleWorkspaceIcon}
                                    fill={
                                        (isIntegrated && isIntegrated.google === true) ||
                                        (isIntegrated && isIntegrated.microsoft === true)
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={jiraIcon}
                                    fill={
                                        isIntegrated && isIntegrated.jira === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                                <Icon
                                    svg={notionIcon}
                                    fill={
                                        isIntegrated && isIntegrated.notion === true
                                            ? "palette.primary.main"
                                            : "palette.background.inactiveIcons"
                                    }
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                    {expanded && (
                        <TeamMemberCardActions member={member} onModalClose={onModalClose} />
                    )}
                </Stack>
            </Box>
        </Card>
    );
};

export default TeamMemberCard;
